import {
  SUGAR_ITEM_DRAG_OVER_EVENT,
  SUGAR_ITEM_DROP_EVENT,
  type SugarItemDragDetail,
} from "../../core/drag/ItemDropElement";
import { ContextConsumer } from "@lit/context";
import { css, html, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { Entity } from "../../engine/Engine";
import { DesignerTransformer } from "../../core/drag/DesignerTransformer";
import { DesignerConfiguratorBridge } from "../../core/configurator/DesignerConfiguratorBridge";
import { createDisposable } from "../../core/events/messaging/index";
import { ViewportCapabilityElement } from "../../core/viewport/ViewportCapabilityElement";
import type { ViewportContext } from "../../core/viewport/ViewportContext";
import type { ConfiguratorStoreMap } from "./ConfiguratorMessageContracts";
import type { ConfiguratorController } from "./SugarConfiguratorController";
import {
  configuratorContext,
  type SugarConfiguratorContext,
} from "./SugarConfiguratorContext";

/**
 * Real CFG viewport capabilities (session + bridge + drag-preview).
 * Product drag/select: shared `<designer-transformer>` (RD stack minus room-designer).
 */
abstract class ConfiguratorViewportCapability extends ViewportCapabilityElement {
  static override styles = css`
    :host {
      display: contents;
    }
  `;

  protected configurator?: SugarConfiguratorContext;
  private storeUnsubscribe?: () => void;
  private readonly configuratorConsumer = new ContextConsumer(this, {
    context: configuratorContext,
    subscribe: true,
    callback: (context) => {
      this.configurator = context;
      this.onConfiguratorContextChanged();
      this.requestUpdate();
    },
  });

  protected override render() {
    void this.configuratorConsumer;
    return html`<slot></slot>`;
  }

  override disconnectedCallback(): void {
    this.storeUnsubscribe?.();
    this.storeUnsubscribe = undefined;
    super.disconnectedCallback();
  }

  protected observeStore<K extends keyof ConfiguratorStoreMap>(
    name: K,
    listener: (state: Readonly<ConfiguratorStoreMap[K]>) => void,
  ): void {
    this.storeUnsubscribe?.();
    this.storeUnsubscribe = this.configurator?.messages
      .store(name)
      .subscribe(listener);
  }

  protected onConfiguratorContextChanged(): void {}
}

/** Domain boot — starts session, wires Room + lifecycle onto `<designer-transformer>`. */
@customElement("configurator-session")
export class ConfiguratorSessionElement extends ConfiguratorViewportCapability {
  @property({ type: String })
  mode: "editor" | "configurator" = "configurator";

  private boundTransformer?: DesignerTransformer;

  protected override onViewportConnected(viewport: ViewportContext) {
    this.startIfReady(viewport);
    this.wireTransformer(viewport);
    this.observeStore("session", (state) => {
      this.dataset.state = state.started ? "started" : "idle";
      this.dataset.mode = state.mode;
      this.wireTransformer(viewport);
    });
    const onEntity = (ev: Event): void => {
      if ((ev as CustomEvent).detail instanceof DesignerTransformer) {
        this.wireTransformer(viewport);
      }
    };
    viewport.host.addEventListener("entity-created", onEntity);
    return createDisposable(() => {
      viewport.host.removeEventListener("entity-created", onEntity);
      this.unwireTransformer(viewport);
    });
  }

  protected override onViewportDisconnected(viewport: ViewportContext): void {
    this.unwireTransformer(viewport);
  }

  protected override onConfiguratorContextChanged(): void {
    if (this.viewport) {
      this.startIfReady(this.viewport);
      this.wireTransformer(this.viewport);
    }
  }

  protected override updated(changed: PropertyValues): void {
    if (changed.has("mode") && this.viewport) {
      this.startIfReady(this.viewport);
    }
  }

  private startIfReady(viewport: ViewportContext): void {
    if (!this.configurator || viewport.disposed) return;
    if (this.configurator.engine === viewport.engine) {
      this.configurator.mode = this.mode;
      return;
    }
    this.configurator.start(viewport.host, this.mode, viewport.engine);
  }

  private unwireTransformer(viewport: ViewportContext): void {
    const controller = findConfiguratorController(viewport);
    if (this.boundTransformer) {
      controller?.unbindDesignerTransformer(this.boundTransformer);
      this.boundTransformer = undefined;
    }
  }

  private wireTransformer(viewport: ViewportContext): void {
    if (this.configurator?.engine !== viewport.engine) return;
    const controller = findConfiguratorController(viewport);
    if (!controller) return;

    const el = viewport.host.querySelector("designer-transformer") as {
      entity?: DesignerTransformer;
      scene?: unknown;
    } | null;
    if (el && !el.scene) {
      (el as { scene: unknown }).scene = controller.productScene;
    }

    const transformer =
      (el?.entity instanceof DesignerTransformer ? el.entity : undefined) ??
      (viewport.engine.entities.find((e) => e instanceof DesignerTransformer) as
        | DesignerTransformer
        | undefined);

    if (!transformer || this.boundTransformer === transformer) return;
    if (this.boundTransformer) {
      controller.unbindDesignerTransformer(this.boundTransformer);
    }
    controller.bindDesignerTransformer(transformer);
    this.boundTransformer = transformer;
  }
}

/**
 * Owns Room-backed {@link DesignerConfiguratorBridge} (same entity RD uses).
 * Composition registers on controller.productScene.
 */
@customElement("configurator-bridge")
export class ConfiguratorBridgeElement extends ConfiguratorViewportCapability {
  private bridge?: DesignerConfiguratorBridge;

  protected override onViewportConnected(viewport: ViewportContext) {
    this._bindBridge(viewport);
    const unsub = this.configurator?.messages.store("session").subscribe(() => {
      this._bindBridge(viewport);
    });
    return createDisposable(() => {
      unsub?.();
      this._teardown(viewport);
    });
  }

  protected override onViewportDisconnected(viewport: ViewportContext): void {
    this._teardown(viewport);
  }

  protected override onConfiguratorContextChanged(): void {
    if (this.viewport) this._bindBridge(this.viewport);
  }

  private _teardown(viewport: ViewportContext): void {
    if (!this.bridge) return;
    viewport.engine.removeEntity(this.bridge);
    this.bridge = undefined;
  }

  private _bindBridge(viewport: ViewportContext): void {
    if (this.configurator?.engine !== viewport.engine) return;
    const controller = findConfiguratorController(viewport);
    if (!controller || this.bridge) return;

    const bridge = new DesignerConfiguratorBridge(
      controller.productScene,
      undefined,
      controller.createCompositionHost(),
    );
    viewport.engine.addEntity(bridge);
    this.bridge = bridge;
  }
}

/**
 * Palette ghost UI + HTML5 drop surface on viewport.host
 * (same target DesignMenu uses — not the WebGL canvas).
 */
@customElement("configurator-drag-preview")
export class ConfiguratorDragPreviewElement extends ConfiguratorViewportCapability {
  protected override onViewportConnected(viewport: ViewportContext) {
    const host = viewport.host;

    // preventDefault / dragenter sayacı / yük çözümleme <sugar-item-drop>'ta.
    // Burada yalnızca "ne yapılacağı" kalıyor.
    const onItemDragOver = (event: Event) => {
      const { originalEvent } = (event as CustomEvent<SugarItemDragDetail>).detail;
      findConfiguratorController(viewport)?.onDragoverOccured(originalEvent);
    };

    const onItemDrop = (event: Event) => {
      const { originalEvent } = (event as CustomEvent<SugarItemDragDetail>).detail;
      findConfiguratorController(viewport)?.onDropOccured(originalEvent);
    };

    host.addEventListener(SUGAR_ITEM_DRAG_OVER_EVENT, onItemDragOver);
    host.addEventListener(SUGAR_ITEM_DROP_EVENT, onItemDrop);

    this.observeStore("dragPreview", (state) => {
      this.dataset.state = state.phase;
      this.toggleAttribute(
        "active",
        state.phase === "preview" ||
          state.phase === "loading" ||
          state.phase === "ready",
      );
    });

    return createDisposable(() => {
      host.removeEventListener(SUGAR_ITEM_DRAG_OVER_EVENT, onItemDragOver);
      host.removeEventListener(SUGAR_ITEM_DROP_EVENT, onItemDrop);
    });
  }
}

function findConfiguratorController(
  viewport: ViewportContext,
): ConfiguratorController | undefined {
  return viewport.engine.entities.find(
    (e: Entity) => e.name === "SugarConfiguratorControllerTarget",
  ) as ConfiguratorController | undefined;
}

declare global {
  interface HTMLElementTagNameMap {
    "configurator-session": ConfiguratorSessionElement;
    "configurator-bridge": ConfiguratorBridgeElement;
    "configurator-drag-preview": ConfiguratorDragPreviewElement;
  }
}
